import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import AmenityForm from "./components/AmenityForm";
import { AmenityFormData } from "./utils/amenitySchema";
import { useCreateAmenity } from "@/components/pages/amenities/hooks/amenities.hooks";
import { fileService } from "@/services/ServiceRegistry";
import { useHotel } from "@/hooks/useHotel";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { hasFrontdeskBlockPermission } from "@/utils/accessControl";
import {
  getAmenityAccess,
  getAmenitySaveErrorKey,
} from "./utils/amenityAccess";

const CreateAmenityPage: React.FC = () => {
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useCreateAmenity();
  const { t } = useTranslation();

  const { hotelId } = useHotel();
  const { userProfile } = useAuthenticatedUser();
  const { canCreate } = getAmenityAccess(
    hasFrontdeskBlockPermission(
      userProfile,
      "frontdesk_hotel_settings",
      "administrate",
    ),
  );
  if (!canCreate) return <Navigate to="/amenities" replace />;
  const handleSubmit = async (
    data: AmenityFormData,
    selectedImage: File | null,
  ) => {
    if (!hotelId) return;
    try {
      let photoId: string | undefined = undefined;
      if (selectedImage) {
        try {
          photoId = await fileService.uploadFileWithPresignedUrl(selectedImage);
        } catch (e) {
          console.error("Failed to upload amenity photo:", e);
        }
      }

      await mutateAsync({
        name: data.name,
        description: data.description,
        category: data.category,
        isActive: data.isActive,
        hotelId,
        photoId,
        serviceIds: data.serviceIds,
        schedules: data.schedules.map((s) => ({
          days: s.days,
          startTime: s.startTime,
          endTime: s.endTime,
        })),
      });

      navigate("/amenities");
    } catch (err) {
      console.error("Failed to create amenity:", err);
      toast.error(t(getAmenitySaveErrorKey(err, "create")));
    }
  };

  return (
    <div className="relative mx-auto max-w-[960px] p-12">
      <Button
        type="button"
        variant="new_outline"
        size="sm"
        onClick={() => navigate("/amenities")}
      >
        <ArrowLeft />
        Back
      </Button>
      <AmenityForm
        titleText={t("amenities.form.createTitle")}
        onSubmit={handleSubmit}
        isSubmitting={isPending}
        submitButtonText={t("amenities.form.create")}
        initialValues={{}}
      />
    </div>
  );
};

export default CreateAmenityPage;
